import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { Sparkles, Zap, Eye, EyeOff, AlertCircle, PartyPopper, Scissors } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'ewrf.launch.unlocked';
const SESSION_KEY = 'ewrf.launch.session';

/** Play a short synthesized thunderclap using Web Audio. No audio asset needed. */
function playThunderclap() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;

    // 1. Low rumble (100ms)
    const rumbleBuffer = ctx.createBuffer(1, ctx.sampleRate * 1.4, ctx.sampleRate);
    const rumbleData = rumbleBuffer.getChannelData(0);
    for (let i = 0; i < rumbleData.length; i++) {
      // Brown noise-like signal for low rumble
      rumbleData[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.35));
    }
    const rumbleSrc = ctx.createBufferSource();
    rumbleSrc.buffer = rumbleBuffer;
    const rumbleFilter = ctx.createBiquadFilter();
    rumbleFilter.type = 'lowpass';
    rumbleFilter.frequency.value = 220;
    const rumbleGain = ctx.createGain();
    rumbleGain.gain.setValueAtTime(0.65, now);
    rumbleGain.gain.exponentialRampToValueAtTime(0.001, now + 1.3);
    rumbleSrc.connect(rumbleFilter).connect(rumbleGain).connect(ctx.destination);
    rumbleSrc.start(now);

    // 2. Sharp crack (white noise burst)
    const crackBuffer = ctx.createBuffer(1, ctx.sampleRate * 0.25, ctx.sampleRate);
    const crackData = crackBuffer.getChannelData(0);
    for (let i = 0; i < crackData.length; i++) {
      crackData[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.03));
    }
    const crackSrc = ctx.createBufferSource();
    crackSrc.buffer = crackBuffer;
    const crackFilter = ctx.createBiquadFilter();
    crackFilter.type = 'highpass';
    crackFilter.frequency.value = 1200;
    const crackGain = ctx.createGain();
    crackGain.gain.setValueAtTime(0.9, now);
    crackGain.gain.exponentialRampToValueAtTime(0.001, now + 0.28);
    crackSrc.connect(crackFilter).connect(crackGain).connect(ctx.destination);
    crackSrc.start(now);
  } catch (_) { /* audio blocked — silent */ }
}

/** Confetti storm — three bursts from different origins for a big launch feel. */
function launchConfetti() {
  const bursts = [
    { angle: 60,  spread: 65, origin: { x: 0.1, y: 0.7 } },
    { angle: 120, spread: 65, origin: { x: 0.9, y: 0.7 } },
    { angle: 90,  spread: 90, origin: { x: 0.5, y: 0.3 } },
  ];
  bursts.forEach((cfg, i) => {
    setTimeout(() => {
      confetti({
        particleCount: 120,
        startVelocity: 55,
        gravity: 0.8,
        ticks: 220,
        scalar: 1.1,
        colors: ['#D4AF37', '#F59E0B', '#1565C0', '#FFFFFF', '#F97316'],
        ...cfg,
      });
    }, i * 220);
  });
  // Follow-up sparkle burst
  setTimeout(() => {
    confetti({
      particleCount: 200,
      spread: 180,
      startVelocity: 25,
      gravity: 0.5,
      origin: { x: 0.5, y: 0.4 },
      colors: ['#D4AF37', '#FFF'],
    });
  }, 900);
}

const RIBBON_THEMES = {
  red: {
    bg: 'bg-gradient-to-br from-[#1a0a0a] via-[#2b0e0e] to-[#4a0d0d]',
    ambientA: 'bg-[#D4AF37]/25',
    ambientB: 'bg-[#B71C1C]/40',
    ribbon: 'bg-gradient-to-r from-[#8B0000] via-[#DC143C] to-[#8B0000]',
    ribbonHalfLeft: 'bg-gradient-to-r from-[#8B0000] via-[#DC143C] to-[#B71C1C]',
    ribbonHalfRight: 'bg-gradient-to-r from-[#B71C1C] via-[#DC143C] to-[#8B0000]',
    ribbonEnd: 'bg-[#8B0000]',
    ribbonShadow: 'shadow-[0_20px_60px_rgba(220,20,60,0.5)]',
    frayed: 'bg-[#4a0d0d]',
    scissorsRing: 'ring-[#4a0d0d]',
    eyebrow: 'text-[#D4AF37]',
  },
  blue: {
    bg: 'bg-gradient-to-br from-[#031024] via-[#062654] to-[#0D47A1]',
    ambientA: 'bg-[#D4AF37]/30',
    ambientB: 'bg-[#1976D2]/40',
    ribbon: 'bg-gradient-to-r from-[#0A1E52] via-[#1565C0] to-[#0A1E52]',
    ribbonHalfLeft: 'bg-gradient-to-r from-[#0A1E52] via-[#1565C0] to-[#1976D2]',
    ribbonHalfRight: 'bg-gradient-to-r from-[#1976D2] via-[#1565C0] to-[#0A1E52]',
    ribbonEnd: 'bg-[#0A1E52]',
    ribbonShadow: 'shadow-[0_20px_60px_rgba(21,101,192,0.55)]',
    frayed: 'bg-[#03153B]',
    scissorsRing: 'ring-[#031024]',
    eyebrow: 'text-[#D4AF37]',
  },
};

const SiteLaunchGate = ({ children }) => {
  const [status, setStatus] = useState('loading'); // loading | locked | cutting | celebrating | open
  const [meta, setMeta] = useState(null);
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);
  const theme = RIBBON_THEMES[meta?.ribbon_theme] || RIBBON_THEMES.red;
  const permanentlyLive = !!(meta?.permanently_live || (meta && meta.enabled === false));

  useEffect(() => {
    // Admin pages MUST bypass the gate so admins can log in and manage the lock
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
      setStatus('open');
      return;
    }
    // Fast path: if session already unlocked, skip everything
    if (sessionStorage.getItem(SESSION_KEY) === '1' || localStorage.getItem(STORAGE_KEY) === '1') {
      setStatus('open');
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/content/site-lock`);
        setMeta(res.data);
        if (!res.data?.enabled) {
          localStorage.setItem(STORAGE_KEY, '1');
          setStatus('open');
        } else {
          setStatus('locked');
        }
      } catch (_) {
        // If the site-lock API fails for any reason, don't lock people out — fail-open
        setStatus('open');
      }
    })();
  }, []);

  useEffect(() => {
    if (status === 'locked') {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
    if (status === 'cutting') {
      // Confetti burst timed with the moment the ribbon splits
      const t = setTimeout(launchConfetti, 900);
      const t2 = setTimeout(playThunderclap, 800);
      return () => { clearTimeout(t); clearTimeout(t2); };
    }
    if (status === 'celebrating') {
      launchConfetti();
      // Cascade a couple more confetti waves during the celebration
      const t1 = setTimeout(launchConfetti, 2200);
      const t2 = setTimeout(launchConfetti, 4200);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [status]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!password.trim() || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/content/site-lock/unlock`, { password: password.trim() });
      const isFinal = !!res.data?.permanently_live;
      // EVERY unlock now grants session access. The final unlock also unlocks the site permanently.
      sessionStorage.setItem(SESSION_KEY, '1');
      if (isFinal) {
        localStorage.setItem(STORAGE_KEY, '1');
      }
      setMeta((m) => (m ? { ...m, ...res.data, permanently_live: isFinal } : res.data));
      // 1) Ribbon-cutting animation (~1.6s) → 2) celebration overlay
      setStatus('cutting');
      setTimeout(() => setStatus('celebrating'), 1600);
    } catch (err) {
      const msg = err?.response?.status === 401
        ? 'Wrong password. Try again.'
        : (err?.response?.data?.detail || 'Something went wrong. Please retry.');
      setError(msg);
      // Shake input
      inputRef.current?.classList.add('animate-[shake_0.4s_ease-in-out]');
      setTimeout(() => inputRef.current?.classList.remove('animate-[shake_0.4s_ease-in-out]'), 500);
    } finally {
      setSubmitting(false);
    }
  };

  // Called by the "Lock the site back" floating pill — clears session & shows the gate again.
  const relockForNextAttempt = () => {
    try {
      sessionStorage.removeItem(SESSION_KEY);
      // Do NOT clear localStorage — if the site is permanently live, keep it that way.
    } catch (_) { /* ignore */ }
    setPassword('');
    setError('');
    // Re-fetch meta so the counter is fresh
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/content/site-lock`);
        setMeta(res.data);
        if (!res.data?.enabled) {
          // If someone else drained the counter to permanent live, don't force a lock
          setStatus('open');
          return;
        }
      } catch (_) { /* keep existing meta */ }
      setStatus('locked');
    })();
  };

  if (status === 'loading') {
    // Show an unobtrusive splash while we check — prevents flash of locked content
    return (
      <div className="fixed inset-0 bg-[#0A1628] flex items-center justify-center z-[9998]">
        <div className="text-white/60 text-sm animate-pulse">Loading…</div>
      </div>
    );
  }

  return (
    <>
      {/* Site content — always mounted so it's ready the moment the gate lifts */}
      <div style={{ visibility: status === 'open' ? 'visible' : 'hidden' }} aria-hidden={status !== 'open'}>
        {children}
      </div>

      {/* Floating "Lock back for next attempt" pill — only while gate is still active and this session unlocked the site */}
      {status === 'open' && !permanentlyLive && meta && (
        <button
          type="button"
          onClick={relockForNextAttempt}
          className="fixed bottom-4 left-4 z-[9990] inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/95 backdrop-blur-md shadow-xl border border-[#D4AF37]/40 text-xs font-bold text-[#0A1628] uppercase tracking-widest hover:bg-white hover:scale-105 transition-all group"
          data-testid="site-relock-pill"
          title={`Lock the site back for the next attempt (${meta.unlocks_remaining ?? 0} left)`}
        >
          <Scissors className="w-3.5 h-3.5 text-[#D4AF37] group-hover:rotate-12 transition-transform" />
          Lock site back
          <span className="ml-1 text-[10px] font-black text-[#D4AF37]">
            {meta.unlocks_remaining ?? 0}/{meta.total_target ?? 9}
          </span>
        </button>
      )}

      {status === 'locked' && (
        <div
          className={`fixed inset-0 z-[9999] flex items-center justify-center px-4 overflow-hidden ${theme.bg}`}
          data-testid="site-launch-gate"
        >
          {/* Ambient stage lights */}
          <div className={`absolute -top-40 left-1/4 w-[500px] h-[500px] ${theme.ambientA} rounded-full blur-3xl pointer-events-none animate-pulse`} />
          <div className={`absolute -bottom-40 right-1/4 w-[500px] h-[500px] ${theme.ambientB} rounded-full blur-3xl pointer-events-none`} />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.15),transparent_60%)] pointer-events-none" />

          {/* Ceremonial ribbon spans the middle of the screen */}
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true">
            <div className="relative w-full">
              {/* Ribbon body with folds (background shading) */}
              <div className={`mx-auto w-full h-16 md:h-20 ${theme.ribbon} ${theme.ribbonShadow} relative overflow-hidden`}>
                {/* Silky highlights */}
                <div className="absolute inset-0 bg-gradient-to-b from-white/25 via-transparent to-black/30" />
                <div className="absolute inset-y-0 left-[15%] w-[2px] bg-white/25 opacity-60" />
                <div className="absolute inset-y-0 right-[15%] w-[2px] bg-white/25 opacity-60" />
                {/* Gold trim */}
                <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
                <div className="absolute bottom-0 inset-x-0 h-[3px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
              </div>
              {/* Ribbon ends (V-cuts) at each side */}
              <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-8 h-16 md:h-20 ${theme.ribbonEnd}`} style={{ clipPath: 'polygon(0 0, 100% 0, 60% 50%, 100% 100%, 0 100%)' }} />
              <div className={`absolute right-0 top-1/2 -translate-y-1/2 w-8 h-16 md:h-20 ${theme.ribbonEnd}`} style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 40% 50%)' }} />
            </div>
          </div>

          <div className="relative w-full max-w-lg" data-testid="launch-gate-card">
            {/* Above-ribbon title block */}
            <div className="text-center mb-24 md:mb-32">
              <div className="inline-flex items-center justify-center gap-2 mb-4">
                <Scissors className={`w-5 h-5 ${theme.eyebrow} rotate-12`} />
                <span className={`text-[10px] font-black uppercase tracking-[0.5em] ${theme.eyebrow}`}>
                  Grand Opening Ceremony
                </span>
                <Scissors className={`w-5 h-5 ${theme.eyebrow} -rotate-12`} />
              </div>
              <h1 className="text-3xl md:text-4xl font-serif text-white mb-2 drop-shadow-lg">
                EWRF Malaysia
              </h1>
              <p className="text-white/75 text-sm md:text-base italic">
                Cut the ribbon to unveil our new home.
              </p>
              <div className="mt-3 text-[11px] text-white/50 tracking-widest uppercase">
                {meta?.unlocks_remaining > 0
                  ? `${meta.unlocks_remaining} of ${meta.total_target || meta.unlocks_remaining} keys left until we go live`
                  : 'Ready to launch'}
              </div>
            </div>

            {/* Password / Key entry — sits below the ribbon */}
            <form
              onSubmit={handleSubmit}
              className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-6 md:p-7 border border-[#D4AF37]/40"
              data-testid="launch-gate-form"
            >
              {/* Big scissors icon (the ceremonial tool) */}
              <div className="absolute -top-8 left-1/2 -translate-x-1/2">
                <div className={`w-16 h-16 rounded-full bg-gradient-to-br from-[#F5E7A2] via-[#D4AF37] to-[#B8942F] shadow-xl flex items-center justify-center ring-4 ${theme.scissorsRing}`}>
                  <Scissors className="w-8 h-8 text-white drop-shadow" strokeWidth={2.5} />
                </div>
              </div>

              <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-2 mt-6 text-center">
                Ceremonial Key
              </label>
              <div className="relative">
                <input
                  ref={inputRef}
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  placeholder="••••••••"
                  autoComplete="off"
                  className="w-full text-lg text-center font-mono tracking-[0.4em] px-4 py-4 rounded-2xl border-2 border-gray-200 focus:border-[#D4AF37] focus:ring-4 focus:ring-[#D4AF37]/20 outline-none transition-all"
                  data-testid="launch-gate-input"
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                  aria-label={showPw ? 'Hide key' : 'Show key'}
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {error && (
                <div className="mt-3 flex items-center justify-center gap-2 text-sm text-red-600" data-testid="launch-gate-error">
                  <AlertCircle className="w-4 h-4" /> {error}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting || !password.trim()}
                className="group mt-5 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#B8942F] hover:from-[#B8942F] hover:to-[#9E7F26] disabled:opacity-50 disabled:cursor-not-allowed text-white font-black uppercase tracking-widest text-sm py-4 rounded-2xl shadow-xl transition-all hover:scale-[1.02]"
                data-testid="launch-gate-submit"
              >
                <Scissors className="w-4 h-4 transition-transform group-hover:rotate-12" />
                {submitting ? 'Cutting the ribbon…' : 'Cut the Ribbon'}
              </button>
            </form>

            <div className="text-center mt-6 text-[11px] text-white/40 tracking-widest uppercase">
              EWRF.ORG.MY · Est. 1979
            </div>
          </div>

          {/* Local shake keyframes */}
          <style>{`
            @keyframes shake { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-8px)} 75%{transform:translateX(8px)} }
          `}</style>
        </div>
      )}

      {status === 'cutting' && (
        <div
          className={`fixed inset-0 z-[9999] flex items-center justify-center px-4 overflow-hidden ${theme.bg}`}
          data-testid="site-launch-cutting"
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.35),transparent_60%)] pointer-events-none" />

          {/* Left half of the ribbon — slides / falls away to the left */}
          <div className={`absolute left-0 top-1/2 -translate-y-1/2 h-16 md:h-20 w-1/2 ${theme.ribbonHalfLeft} ${theme.ribbonShadow} origin-right animate-[ribbonLeft_1400ms_cubic-bezier(0.5,0,0.75,0)_forwards]`}>
            <div className="absolute inset-0 bg-gradient-to-b from-white/25 via-transparent to-black/30" />
            <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
            <div className="absolute bottom-0 inset-x-0 h-[3px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
            {/* Frayed cut edge */}
            <div className={`absolute right-0 top-0 bottom-0 w-3 ${theme.frayed}`} style={{ clipPath: 'polygon(0 0, 100% 20%, 30% 50%, 100% 80%, 0 100%)' }} />
          </div>
          {/* Right half — slides / falls away to the right */}
          <div className={`absolute right-0 top-1/2 -translate-y-1/2 h-16 md:h-20 w-1/2 ${theme.ribbonHalfRight} ${theme.ribbonShadow} origin-left animate-[ribbonRight_1400ms_cubic-bezier(0.5,0,0.75,0)_forwards]`}>
            <div className="absolute inset-0 bg-gradient-to-b from-white/25 via-transparent to-black/30" />
            <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
            <div className="absolute bottom-0 inset-x-0 h-[3px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
            <div className={`absolute left-0 top-0 bottom-0 w-3 ${theme.frayed}`} style={{ clipPath: 'polygon(100% 0, 0 20%, 70% 50%, 0 80%, 100% 100%)' }} />
          </div>

          {/* Big scissors flash in the middle */}
          <div className="relative z-10 animate-[scissorsCut_1000ms_ease-out_forwards]">
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-gradient-to-br from-[#F5E7A2] via-[#D4AF37] to-[#B8942F] shadow-[0_0_60px_rgba(212,175,55,0.8)] flex items-center justify-center ring-8 ring-white/20">
              <Scissors className="w-12 h-12 md:w-16 md:h-16 text-white" strokeWidth={2.5} />
            </div>
          </div>

          <style>{`
            @keyframes ribbonLeft { 
              0% { transform: translateX(0) rotate(0); opacity: 1; }
              40% { transform: translateX(-4%) rotate(-1deg); opacity: 1; }
              100% { transform: translateX(-110%) rotate(-25deg) translateY(60vh); opacity: 0; }
            }
            @keyframes ribbonRight {
              0% { transform: translateX(0) rotate(0); opacity: 1; }
              40% { transform: translateX(4%) rotate(1deg); opacity: 1; }
              100% { transform: translateX(110%) rotate(25deg) translateY(60vh); opacity: 0; }
            }
            @keyframes scissorsCut {
              0% { transform: scale(0.4) rotate(-30deg); opacity: 0; }
              30% { transform: scale(1.3) rotate(10deg); opacity: 1; }
              60% { transform: scale(1.15) rotate(-10deg); opacity: 1; }
              100% { transform: scale(1) rotate(0); opacity: 1; }
            }
          `}</style>
        </div>
      )}

      {status === 'celebrating' && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center px-4 bg-gradient-to-br from-[#0A1628] via-[#0D2444] to-[#1A365D] animate-[fadeIn_500ms_ease-out]"
          data-testid="site-launch-celebration"
        >
          {/* Ambient */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.25),transparent_60%)] pointer-events-none" />
          <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-[#D4AF37]/25 rounded-full blur-3xl pointer-events-none animate-pulse" />

          <div className="relative w-full max-w-2xl text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-[10px] font-black uppercase tracking-[0.4em] text-[#FFE58A] mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Launched
            </div>

            <PartyPopper className="w-16 h-16 md:w-20 md:h-20 text-[#D4AF37] mx-auto mb-6 drop-shadow-[0_0_20px_rgba(212,175,55,0.6)] animate-bounce" />

            <div className="text-white/70 text-lg md:text-xl font-light mb-3 animate-[fadeUp_800ms_ease-out_forwards] opacity-0" style={{ animationDelay: '200ms' }}>
              {meta?.welcome_title || 'Welcome to the new'}
            </div>
            <h1 className="text-5xl md:text-7xl font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#F5E7A2] via-[#D4AF37] to-[#F59E0B] mb-6 drop-shadow-2xl animate-[fadeUp_900ms_ease-out_forwards] opacity-0" style={{ animationDelay: '500ms' }}>
              {meta?.welcome_brand || 'EWRF.ORG.MY'}
            </h1>
            <p className="text-white/85 text-base md:text-lg max-w-xl mx-auto mb-8 animate-[fadeUp_1000ms_ease-out_forwards] opacity-0" style={{ animationDelay: '800ms' }}>
              {meta?.welcome_subtitle || 'The Educational, Welfare and Research Foundation Malaysia — celebrating 47 years of impact, now online.'}
            </p>

            <button
              type="button"
              onClick={() => setStatus('open')}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#B8942F] hover:from-[#B8942F] hover:to-[#9E7F26] text-white font-black uppercase tracking-widest text-sm px-8 py-4 rounded-full shadow-2xl hover:shadow-amber-500/50 hover:scale-105 transition-all animate-[fadeUp_1100ms_ease-out_forwards] opacity-0"
              style={{ animationDelay: '1100ms' }}
              data-testid="launch-celebration-enter-btn"
            >
              <Zap className="w-4 h-4" />
              Enter Website
            </button>

            {permanentlyLive ? (
              <div className="mt-6 text-xs text-[#FFE58A]/90 italic animate-[fadeUp_1200ms_ease-out_forwards] opacity-0" style={{ animationDelay: '1400ms' }}>
                🎉 Final ribbon cut — the site is now permanently live for everyone!
              </div>
            ) : (
              <div className="mt-6 text-xs text-white/60 tracking-widest uppercase animate-[fadeUp_1200ms_ease-out_forwards] opacity-0" style={{ animationDelay: '1400ms' }}>
                <span className="text-[#FFE58A]">{meta?.remaining ?? meta?.unlocks_remaining ?? 0}</span> ribbons left · you can lock the site back anytime for the next attempt
              </div>
            )}
          </div>

          <style>{`
            @keyframes fadeIn { from{opacity:0} to{opacity:1} }
            @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
          `}</style>
        </div>
      )}
    </>
  );
};

export default SiteLaunchGate;
